import React, { useState, useEffect } from 'react';
import '../styles/DijkstraVisualizer.css';

function DijkstraVisualizer({ graph }) {
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [mode, setMode] = useState('manual');
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (graph && graph.nodes && graph.nodes.length > 0) {
      runDijkstra(graph);
    }
  }, [graph]);

  useEffect(() => {
    if (mode === 'automatic') {
      const id = setInterval(() => {
        setCurrentStep((prevStep) => {
          if (prevStep < steps.length - 1) {
            return prevStep + 1;
          } else {
            clearInterval(id);
            return prevStep;
          }
        });
      }, 2000);
      setIntervalId(id);
      return () => clearInterval(id);
    }
  }, [mode, steps]);

  const runDijkstra = ({ nodes, edges }) => {
    const stepsArray = [];
    const distances = {};
    const previous = {};
    const unvisited = new Set(nodes.map(node => node.id));

    nodes.forEach(node => {
      distances[node.id] = Infinity;
      previous[node.id] = null;
    });

    const startNode = nodes[0].id;
    distances[startNode] = 0;

    while (unvisited.size > 0) {
      const currentNode = Array.from(unvisited).reduce((minNode, node) => (
        distances[node] < distances[minNode] ? node : minNode
      ));

      unvisited.delete(currentNode);

      edges
        .filter(edge => edge.from === currentNode)
        .forEach(edge => {
          const newDist = distances[currentNode] + edge.weight;
          if (newDist < distances[edge.to]) {
            distances[edge.to] = newDist;
            previous[edge.to] = currentNode;
          }
        });

      stepsArray.push({
        distances: { ...distances },
        previous: { ...previous },
        currentNode
      });
    }

    setSteps(stepsArray);
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleModeToggle = () => {
    if (mode === 'manual') {
      setMode('automatic');
    } else {
      setMode('manual');
      clearInterval(intervalId);
    }
  };

  const currentDistances = steps.length > 0 ? steps[currentStep].distances : {};
  const currentPrevious = steps.length > 0 ? steps[currentStep].previous : {};
  const currentNode = steps.length > 0 ? steps[currentStep].currentNode : null;

  return (
    <div>
      <h3>Dijkstra Algorithm Visualization</h3>
      <div className="dijkstra-visualizer">
        <div className="graph-container">
          {graph.nodes.map(node => (
            <div
              key={node.id}
              className={`dijkstra-node ${node.id === currentNode ? 'visited' : ''}`}
              style={{
                backgroundColor: node.id === currentNode ? '#ff5722' : '#007bff',
                borderColor: node.id === currentNode ? '#ff5722' : '#007bff'
              }}
            >
              {node.id} ({currentDistances[node.id] !== undefined ? currentDistances[node.id] : '∞'})
            </div>
          ))}
        </div>
        <div className="controls">
          <button onClick={handleModeToggle}>
            {mode === 'manual' ? 'Start Automatic' : 'Start Manual'}
          </button>
          {mode === 'manual' && (
            <>
              <button onClick={handlePreviousStep}>Previous Step</button>
              <button onClick={handleNextStep}>Next Step</button>
            </>
          )}
        </div>
        <div className="step-details">
          <p>Current Step: {currentStep + 1} / {steps.length}</p>
          <p>Current Node: {currentNode}</p>
          <p>Distances: {JSON.stringify(currentDistances)}</p>
          <p>Previous Nodes: {JSON.stringify(currentPrevious)}</p>
        </div>
        <div className="dijkstra-explanation">
          <h4>How Dijkstra's Algorithm Works:</h4>
          <ol>
            <li>
              <strong>Initialization:</strong> Start with the initial node and set the distance to 0. Set all other nodes to infinity.
            </li>
            <li>
              <strong>Visit Nodes:</strong> At each step, visit the unvisited node with the smallest known distance from the start node.
            </li>
            <li>
              <strong>Update Distances:</strong> For the current node, consider all its unvisited neighbors and calculate their tentative distances through the current node.
            </li>
            <li>
              <strong>Mark Visited:</strong> Once all neighbors of the current node are considered, mark the current node as visited.
            </li>
            <li>
              <strong>Repeat:</strong> Continue this process until all nodes have been visited.
            </li>
          </ol>
          <p>
            Dijkstra's Algorithm is an efficient method for finding the shortest path from a single source node to all other nodes in a weighted graph. It ensures the shortest path is found by exploring nodes in order of their distance from the source, using a priority queue to efficiently manage the exploration process.
          </p>
        </div>
      </div>
    </div>
  );
}

export default DijkstraVisualizer;
